import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SeoManager, getPageSeo, buildFAQSchema } from '../seo';
import FAQSection, { stopTheWheelFaqs } from '../components/FAQSection';
import {
  TOTAL_LEVELS,
  angularError,
  getLevelConfig,
  clampLevel,
  isTargetHit,
  scoreFromAngleError,
  getWheelRank,
} from '../utils/wheelScore';
import { getItem, setItem } from '../utils/safeStorage';
import GameGuideLink from '../components/GameGuideLink';
import '../styles/StopTheWheel.css';

const HIGHSCORE_KEY = 'stw-stop-wheel-highscore';
const LEVEL_KEY = 'stw-stop-wheel-level';
const BEST_LEVEL_KEY = 'stw-stop-wheel-best-level';

const readStoredNumber = (key) => {
  const stored = Number(getItem(key));
  return Number.isFinite(stored) ? stored : null;
};

const writeStoredNumber = (key, value) => {
  setItem(key, String(value));
};

export default function StopTheWheelPage() {
  const [phase, setPhase] = useState('idle');
  const [level, setLevel] = useState(1);
  const [bestLevel, setBestLevel] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [score, setScore] = useState(null);
  const [errorDeg, setErrorDeg] = useState(null);
  const [hit, setHit] = useState(null);
  const [clearedAll, setClearedAll] = useState(false);
  const [highscore, setHighscore] = useState(0);
  const [resultRank, setResultRank] = useState(null);
  const rotationRef = useRef(0);
  const speedRef = useRef(0);
  const frameRef = useRef(null);
  const lastTimeRef = useRef(null);
  const wheelRef = useRef(null);

  const config = getLevelConfig(level);

  useEffect(() => {
    const storedScore = readStoredNumber(HIGHSCORE_KEY);
    if (storedScore != null && storedScore > 0) setHighscore(storedScore);

    const storedLevel = readStoredNumber(LEVEL_KEY);
    if (storedLevel != null) setLevel(clampLevel(storedLevel));

    const storedBest = readStoredNumber(BEST_LEVEL_KEY);
    if (storedBest != null && storedBest > 0) setBestLevel(clampLevel(storedBest));
  }, []);

  const stopAnimation = useCallback(() => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    lastTimeRef.current = null;
  }, []);

  useEffect(() => () => {
    stopAnimation();
  }, [stopAnimation]);

  const saveHighscore = useCallback((nextScore) => {
    setHighscore((best) => {
      if (nextScore <= best) return best;
      writeStoredNumber(HIGHSCORE_KEY, nextScore);
      return nextScore;
    });
  }, []);

  const saveBestLevel = useCallback((clearedLevel) => {
    setBestLevel((best) => {
      if (clearedLevel <= best) return best;
      writeStoredNumber(BEST_LEVEL_KEY, clearedLevel);
      return clearedLevel;
    });
  }, []);

  const applyWheelStyle = useCallback((rotationDeg, sectorDeg) => {
    if (!wheelRef.current) return;
    wheelRef.current.style.setProperty('--stw-from', `${-sectorDeg / 2}deg`);
    wheelRef.current.style.setProperty('--stw-sector', `${sectorDeg}deg`);
    wheelRef.current.style.transform = `rotate(${rotationDeg}deg)`;
  }, []);

  const tick = useCallback((timestamp) => {
    if (lastTimeRef.current == null) {
      lastTimeRef.current = timestamp;
    }
    const delta = (timestamp - lastTimeRef.current) / 1000;
    lastTimeRef.current = timestamp;

    rotationRef.current += speedRef.current * delta;
    const nextRotation = rotationRef.current;
    setRotation(nextRotation);
    if (wheelRef.current) {
      wheelRef.current.style.transform = `rotate(${nextRotation}deg)`;
    }

    frameRef.current = requestAnimationFrame(tick);
  }, []);

  const startRound = useCallback((options = {}) => {
    const restart = options.restart === true;
    let nextLevel = restart ? 1 : level;
    if (!restart && hit && level < TOTAL_LEVELS) {
      nextLevel = level + 1;
    }

    stopAnimation();
    setLevel(nextLevel);
    writeStoredNumber(LEVEL_KEY, nextLevel);

    const nextConfig = getLevelConfig(nextLevel);
    rotationRef.current = Math.random() * 360;
    setRotation(rotationRef.current);
    speedRef.current = nextConfig.speedDps;
    setScore(null);
    setErrorDeg(null);
    setHit(null);
    setClearedAll(false);
    setResultRank(null);
    setPhase('spinning');

    applyWheelStyle(rotationRef.current, nextConfig.sectorDeg);
    frameRef.current = requestAnimationFrame(tick);
  }, [applyWheelStyle, hit, level, stopAnimation, tick]);

  const restartFromOne = useCallback(() => {
    startRound({ restart: true });
  }, [startRound]);

  const stopWheel = useCallback(() => {
    if (phase !== 'spinning') return;

    stopAnimation();
    const error = angularError(rotationRef.current);
    const nextScore = scoreFromAngleError(error, config.sectorDeg);
    const didHit = isTargetHit(error, config.sectorDeg);

    setErrorDeg(Math.round(error * 10) / 10);
    setScore(nextScore);
    setHit(didHit);
    saveHighscore(nextScore);

    const allClear = didHit && level >= TOTAL_LEVELS;
    setResultRank(getWheelRank({ score: nextScore, hit: didHit, clearedAll: allClear }));

    if (didHit) {
      saveBestLevel(level);
      if (level >= TOTAL_LEVELS) {
        setClearedAll(true);
        writeStoredNumber(LEVEL_KEY, 1);
      } else {
        writeStoredNumber(LEVEL_KEY, level + 1);
      }
    } else {
      writeStoredNumber(LEVEL_KEY, 1);
    }

    setPhase('result');
  }, [config.sectorDeg, level, phase, saveBestLevel, saveHighscore, stopAnimation]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code !== 'Space' && event.code !== 'Enter') return;
      if (phase === 'spinning') {
        event.preventDefault();
        stopWheel();
      } else if (phase === 'result') {
        event.preventDefault();
        if (hit && !clearedAll) startRound();
        else restartFromOne();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [clearedAll, hit, phase, restartFromOne, startRound, stopWheel]);

  const seo = getPageSeo('stop-the-wheel');
  const structuredData = seo?.structuredData
    ? [seo.structuredData, buildFAQSchema(stopTheWheelFaqs)]
    : buildFAQSchema(stopTheWheelFaqs);

  const wheelVars = {
    '--stw-from': `${-config.sectorDeg / 2}deg`,
    '--stw-sector': `${config.sectorDeg}deg`,
  };

  const resultTitle = clearedAll
    ? 'All 30 levels cleared!'
    : hit
      ? `Level ${level} cleared`
      : 'Game Over';

  const primaryResultAction = clearedAll
    ? { label: 'Play Again from Level 1', onClick: restartFromOne }
    : hit
      ? { label: `Start Level ${level + 1}`, onClick: () => startRound() }
      : { label: 'Play Again', onClick: restartFromOne };

  return (
    <>
      <SeoManager
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        structuredData={structuredData}
      />

      <section className="stw-game" aria-label="Stop the Wheel reaction game">
        <h1 className="stw-visually-hidden">Stop the Wheel: Reaction Accuracy Test</h1>

        <header className="stw-header">
          <div className="stw-score-block">
            <span className="stw-score-number">{level}/{TOTAL_LEVELS}</span>
            <span className="stw-score-label">Level</span>
          </div>
          <div className="stw-score-block">
            <span className="stw-score-number">{bestLevel || '—'}</span>
            <span className="stw-score-label">Best Level</span>
          </div>
          <div className="stw-score-block">
            <span className="stw-score-number">{highscore}</span>
            <span className="stw-score-label">Best Score</span>
          </div>
          {score != null && (
            <div className="stw-score-block">
              <span className="stw-score-number">{score}</span>
              <span className="stw-score-label">Last Score</span>
            </div>
          )}
        </header>

        <div className="stw-stage">
          {phase === 'idle' && (
            <div className="stw-idle-card">
              <h2>Stop the Wheel</h2>
              <p>
                30 levels. The purple sector starts already fast, then gets
                smaller and quicker each time you hit it. Miss once and the run
                is over — back to level 1.
              </p>
              <button type="button" className="stw-btn stw-btn-primary" onClick={() => startRound()}>
                {level > 1 ? `Continue Level ${level}` : 'Start Level 1'}
              </button>
              {level > 1 && (
                <button type="button" className="stw-btn stw-btn-ghost" onClick={restartFromOne}>
                  Start over from Level 1
                </button>
              )}
            </div>
          )}

          {(phase === 'spinning' || phase === 'result') && (
            <>
              <p className="stw-level-indicator" aria-live="polite">
                Level {level} / {TOTAL_LEVELS}
              </p>
              <div
                className="stw-wheel-wrap"
                data-stw-level={level}
                data-stw-sector={config.sectorDeg}
                data-stw-speed={config.speedDps}
              >
                <div className="stw-pointer" aria-hidden="true" />
                <div
                  ref={wheelRef}
                  className="stw-wheel"
                  style={{ ...wheelVars, transform: `rotate(${rotation}deg)` }}
                  aria-hidden="true"
                />
                <div className="stw-wheel-hub" aria-hidden="true" />
              </div>

              {phase === 'spinning' && (
                <>
                  <p className="stw-spin-hint">Stop when the purple zone hits the pointer</p>
                  <button
                    type="button"
                    className="stw-btn stw-btn-primary stw-stop-btn"
                    onClick={stopWheel}
                  >
                    Stop!
                  </button>
                </>
              )}
            </>
          )}

          {phase === 'result' && score != null && (
            <div className={`stw-result-card ${hit ? 'stw-result-card-hit' : 'stw-result-card-miss'}`}>
              <h2>{resultTitle}</h2>
              <p className="stw-result-score">{score}/100</p>
              {resultRank && (
                <>
                  <div className="stw-rank-row">
                    <span className="stw-rank-label">Your Rank:</span>
                    <span className="stw-rank-animal">{resultRank.animal}</span>
                  </div>
                  <p className="stw-result-label">{resultRank.message}</p>
                </>
              )}
              <p className="stw-result-meta">
                You were {errorDeg}° off the target center.
                {hit
                  ? clearedAll
                    ? ' You beat every level.'
                    : ' Next up: a smaller sector and a faster spin.'
                  : ' You lost. Start over from level 1.'}
              </p>
              <button
                type="button"
                className="stw-btn stw-btn-primary"
                onClick={primaryResultAction.onClick}
              >
                {primaryResultAction.label}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="stw-extras">
        <h2>How to Play Stop the Wheel</h2>
        <p>
          Watch the spinning wheel and click Stop (or press Space) when the
          purple segment aligns with the pointer. Land inside the sector to
          clear the level. A perfect stop scores 100. Miss the zone and the
          run is over — you start again from level 1.
        </p>
        <ul>
          <li>There are 30 levels. Early levels are already quick; each hit shrinks the target and speeds the spin.</li>
          <li>Tap Stop, click Stop, or press Space / Enter to freeze the wheel.</li>
          <li>One miss ends the run. Your best level and best score are saved in this browser.</li>
        </ul>
      </section>

      <GameGuideLink slug="stop-the-wheel" gameName="Stop the Wheel" />
      <FAQSection mode="stopTheWheel" />
    </>
  );
}
